/**
 * EVENTSFLO-TASK-DEFINITIONS-EXPORT-DEPENDENCY-REPORT-V1
 *
 * Settings → Task Definitions Dependency Report (read-only)
 *
 * Phase 1 surface for the new dependency-report export. Two CTAs:
 *   1. Refresh preview — calls
 *      GET /api/admin/task-definitions/export-dependency-report (JSON)
 *      so the admin sees recommended_action counts BEFORE downloading.
 *   2. Download XLSX — fetches
 *      GET /api/admin/task-definitions/export-dependency-report.xlsx
 *      with the active Bearer token and saves the workbook via
 *      `URL.createObjectURL` so the filename + binary survive.
 *
 * The page is intentionally read-only — no inserts, no updates, no
 * deletes. This is also the property the backend service guarantees,
 * so the UI just surfaces the live counts and a download CTA.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  getTaskDefinitionDependencyReport,
  downloadTaskDefinitionDependencyReportXlsx,
  taskDefinitionImportDryRun,
  taskDefinitionImportApply,
  taskDefinitionImportAudit,
  extractErrorMessage,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Loader2,
  Download,
  RefreshCcw,
  FileSpreadsheet,
  ShieldCheck,
  AlertTriangle,
  Trash2,
  Eye,
  Upload,
  Beaker,
  X,
  HelpCircle,
  CheckCircle2,
  History,
} from "lucide-react";

const ACTION_META = {
  protected: {
    label: "Protected — system seed",
    icon: ShieldCheck,
    cls: "border-amber-500/40 text-amber-200 bg-amber-500/10",
  },
  keep: {
    label: "Keep — live dependencies",
    icon: ShieldCheck,
    cls: "border-emerald-500/40 text-emerald-200 bg-emerald-500/10",
  },
  review: {
    label: "Review — active, no live refs",
    icon: Eye,
    cls: "border-rose-500/40 text-rose-200 bg-rose-500/10",
  },
  can_deactivate_if_unused: {
    label: "Can deactivate if unused",
    icon: AlertTriangle,
    cls: "border-indigo-500/40 text-indigo-200 bg-indigo-500/10",
  },
  safe_delete_candidate: {
    label: "Safe delete candidate",
    icon: Trash2,
    cls: "border-indigo-500/40 text-indigo-200 bg-indigo-500/10",
  },
};

function ActionBadge({ action }) {
  const meta = ACTION_META[action] || {
    label: action || "—",
    icon: AlertTriangle,
    cls: "border-slate-500/40 text-slate-200 bg-slate-500/10",
  };
  const Icon = meta.icon;
  return (
    <Badge
      variant="outline"
      className={meta.cls}
      data-testid={`td-dep-badge-${action || "unknown"}`}
    >
      <Icon className="w-3 h-3 mr-1" />
      {meta.label}
    </Badge>
  );
}

function _triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so Safari finishes the navigation.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function _filenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback;
  const m = /filename="?([^"]+)"?/i.exec(disposition);
  return m ? m[1] : fallback;
}

export default function TaskDefinitionDependencyReportPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [includeSeeds, setIncludeSeeds] = useState(true);

  // EVENTSFLO-TASK-DEFINITIONS-IMPORT-DRY-RUN-PREVIEW-V1
  const [dryRunFile, setDryRunFile] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // EVENTSFLO-TASK-DEFINITIONS-IMPORT-APPLY-SAFE-CHANGES-V1
  const [helpOpen, setHelpOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAck, setConfirmAck] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditBusy, setAuditBusy] = useState(false);

  const SELECTABLE_STATUSES = useMemo(
    () => new Set(["proposed_update", "proposed_deactivate", "proposed_create"]),
    []
  );

  const toggleRow = (rowNumber) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };

  const selectableRowNumbers = useMemo(() => {
    if (!dryRunResult) return [];
    return (dryRunResult.rows || [])
      .filter((r) => SELECTABLE_STATUSES.has(r.status))
      .map((r) => r.row_number);
  }, [dryRunResult, SELECTABLE_STATUSES]);

  const toggleSelectAll = () => {
    if (selectedRows.size === selectableRowNumbers.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(selectableRowNumbers));
    }
  };

  const openConfirm = () => {
    if (selectedRows.size === 0) {
      toast.error("Pick at least one selectable row first.");
      return;
    }
    setConfirmAck(false);
    setConfirmOpen(true);
  };

  const runApply = async () => {
    if (!dryRunFile) {
      toast.error("Re-upload the workbook before applying.");
      return;
    }
    setApplyBusy(true);
    try {
      const { data } = await taskDefinitionImportApply({
        file: dryRunFile,
        selectedRowNumbers: Array.from(selectedRows),
        confirmed: true,
      });
      setApplyResult(data);
      setConfirmOpen(false);
      setSelectedRows(new Set());
      toast.success(
        `Applied ${data.summary.applied} · Skipped ${data.summary.skipped} · ` +
          `Failed ${data.summary.failed}.`
      );
      // Refresh dependency report so admins see post-apply state.
      loadReport();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Apply failed"));
    } finally {
      setApplyBusy(false);
    }
  };

  const loadAudit = async () => {
    setAuditBusy(true);
    try {
      const { data } = await taskDefinitionImportAudit(100);
      setAuditRows(data.rows || []);
      setAuditOpen(true);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not load audit log"));
    } finally {
      setAuditBusy(false);
    }
  };

  const onPickDryRunFile = (e) => {
    const f = e.target.files?.[0];
    setDryRunFile(f || null);
    setDryRunResult(null);
  };

  const clearDryRun = () => {
    setDryRunFile(null);
    setDryRunResult(null);
  };

  const runDryRun = async () => {
    if (!dryRunFile) {
      toast.error("Pick an .xlsx workbook first.");
      return;
    }
    setDryRunBusy(true);
    setDryRunResult(null);
    try {
      const { data } = await taskDefinitionImportDryRun(dryRunFile);
      setDryRunResult(data);
      const s = data.summary || {};
      const change =
        (s.proposed_create || 0) +
        (s.proposed_update || 0) +
        (s.proposed_deactivate || 0) +
        (s.proposed_delete || 0);
      toast.success(
        `Dry-run complete — ${s.unchanged || 0} unchanged · ` +
          `${change} proposed change(s) · ${s.blocked || 0} blocked · ` +
          `${s.invalid || 0} invalid. No DB writes performed.`
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Dry-run failed"));
    } finally {
      setDryRunBusy(false);
    }
  };

  const filteredDryRunRows = useMemo(() => {
    if (!dryRunResult) return [];
    const rows = dryRunResult.rows || [];
    if (statusFilter === "all") return rows;
    if (statusFilter === "changes") {
      return rows.filter((r) =>
        [
          "proposed_create",
          "proposed_update",
          "proposed_deactivate",
          "proposed_delete",
          "blocked",
          "invalid",
        ].includes(r.status)
      );
    }
    return rows.filter((r) => r.status === statusFilter);
  }, [dryRunResult, statusFilter]);

  const downloadDryRunJson = () => {
    if (!dryRunResult) return;
    const blob = new Blob([JSON.stringify(dryRunResult, null, 2)], {
      type: "application/json",
    });
    _triggerBlobDownload(
      blob,
      `task-definitions-dry-run-${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.json`
    );
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getTaskDefinitionDependencyReport({
        include_system_seeds: includeSeeds,
      });
      setReport(data);
      toast.success(
        `Loaded ${data.total_tenant_rows} tenant task definition(s).`
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to load dependency report"));
    } finally {
      setLoading(false);
    }
  }, [includeSeeds]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await downloadTaskDefinitionDependencyReportXlsx({
        include_system_seeds: includeSeeds,
      });
      const filename = _filenameFromDisposition(
        resp.headers?.["content-disposition"],
        "task-definitions-dependency-report.xlsx"
      );
      _triggerBlobDownload(resp.data, filename);
      toast.success("Dependency report downloaded.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to download XLSX"));
    } finally {
      setDownloading(false);
    }
  };

  const actionCounts = useMemo(() => {
    if (!report) return {};
    const out = {};
    const allRows = [
      ...(report.rows || []),
      ...(report.system_seed_rows || []),
    ];
    for (const r of allRows) {
      const a = r.recommended_action || "unknown";
      out[a] = (out[a] || 0) + 1;
    }
    return out;
  }, [report]);

  const previewRows = useMemo(() => {
    if (!report) return [];
    return (report.rows || []).slice(0, 30);
  }, [report]);

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl" data-testid="task-def-dep-report-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="td-import-page-header">
            <FileSpreadsheet className="w-7 h-7 text-amber-400" />
            Task Definitions Export / Import
          </h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-3xl">
            Export catalogue, review dependencies, upload workbook for
            dry-run validation, and apply selected safe changes. The
            dry-run preview is the source of truth — Apply re-runs it
            server-side before any write.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge
            variant="outline"
            className="border-emerald-500/40 text-emerald-200 bg-emerald-500/10 whitespace-nowrap"
            data-testid="td-dep-readonly-badge"
          >
            <ShieldCheck className="w-3 h-3 mr-1" />
            Export is read-only · Apply writes selected safe rows only
          </Badge>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHelpOpen(true)}
              data-testid="td-import-help-btn"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              How this works
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadAudit}
              disabled={auditBusy}
              data-testid="td-import-audit-btn"
            >
              {auditBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <History className="w-4 h-4 mr-2" />
              )}
              Audit log
            </Button>
          </div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Generate report</CardTitle>
          <CardDescription>
            The export is scoped to your tenant. System seeds (global
            templates) are included by default and are clearly tagged
            <span className="font-semibold"> Protected</span> so they cannot be
            confused with tenant rows.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 text-sm">
            <Checkbox
              checked={includeSeeds}
              onCheckedChange={(v) => setIncludeSeeds(Boolean(v))}
              data-testid="td-dep-include-seeds"
            />
            <span>Include protected system seeds (global templates)</span>
          </label>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={loadReport}
              variant="outline"
              disabled={loading}
              data-testid="td-dep-refresh-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCcw className="w-4 h-4 mr-2" />
              )}
              Refresh preview
            </Button>

            <Button
              onClick={handleDownload}
              disabled={downloading || loading}
              data-testid="td-dep-download-xlsx-btn"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download XLSX
            </Button>
          </div>
        </CardContent>
      </Card>

      {report && (
        <>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Summary</CardTitle>
              <CardDescription>
                Generated at {report.generated_at} ·{" "}
                <span className="font-semibold">{report.total_tenant_rows}</span>{" "}
                tenant row(s) ·{" "}
                <span className="font-semibold">
                  {report.total_system_seed_rows}
                </span>{" "}
                system seed(s).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3" data-testid="td-dep-action-counts">
                {Object.entries(actionCounts).map(([action, count]) => (
                  <div
                    key={action}
                    className="flex items-center gap-2"
                    data-testid={`td-dep-action-count-${action}`}
                  >
                    <ActionBadge action={action} />
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                ))}
                {Object.keys(actionCounts).length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No task definitions found for this tenant.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                Preview — first {previewRows.length} tenant row(s)
              </CardTitle>
              <CardDescription>
                Download the XLSX for the full list, dependency summary, and
                import guidance.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="td-dep-preview-table">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Active</th>
                    <th className="py-2 pr-3">Action</th>
                    <th className="py-2 pr-3">Total deps</th>
                    <th className="py-2 pr-3">Warnings</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b last:border-b-0 align-top"
                      data-testid={`td-dep-row-${r.id}`}
                    >
                      <td className="py-2 pr-3 font-medium">{r.name}</td>
                      <td className="py-2 pr-3">
                        {r.is_active ? "yes" : "no"}
                      </td>
                      <td className="py-2 pr-3">
                        <ActionBadge action={r.recommended_action} />
                      </td>
                      <td className="py-2 pr-3">
                        {r.total_dependency_count || 0}
                      </td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">
                        {(r.warnings || []).join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
                  {previewRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-4 text-center text-muted-foreground">
                        No tenant task definitions.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {/* EVENTSFLO-TASK-DEFINITIONS-IMPORT-DRY-RUN-PREVIEW-V1 */}
      <Card className="mt-8" data-testid="td-dryrun-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Beaker className="w-5 h-5 text-indigo-400" />
            Upload edited workbook (Dry-run preview)
          </CardTitle>
          <CardDescription>
            Upload a Task Definitions workbook (downloaded above and
            edited locally) to see exactly what would change.{" "}
            <span className="font-semibold text-amber-300">
              Dry-run only — no changes will be applied.
            </span>{" "}
            Apply / Import is intentionally not exposed in this phase.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onPickDryRunFile}
              data-testid="td-dryrun-file-input"
              className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-indigo-500/10 file:text-indigo-200 file:cursor-pointer"
            />
            {dryRunFile && (
              <span
                className="text-xs text-muted-foreground"
                data-testid="td-dryrun-file-name"
              >
                {dryRunFile.name} · {(dryRunFile.size / 1024).toFixed(1)} KB
              </span>
            )}
            <Button
              onClick={runDryRun}
              disabled={!dryRunFile || dryRunBusy}
              data-testid="td-dryrun-run-btn"
            >
              {dryRunBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Run dry-run preview
            </Button>
            {(dryRunFile || dryRunResult) && (
              <Button
                variant="ghost"
                onClick={clearDryRun}
                data-testid="td-dryrun-clear-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Clear upload
              </Button>
            )}
          </div>

          {dryRunResult && (
            <div className="space-y-4 pt-2" data-testid="td-dryrun-result">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge
                  variant="outline"
                  className="border-amber-500/40 text-amber-200 bg-amber-500/10"
                  data-testid="td-dryrun-noop-badge"
                >
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  Dry-run only — no DB writes
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Generated at {dryRunResult.generated_at}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadDryRunJson}
                  data-testid="td-dryrun-download-json-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download JSON
                </Button>
              </div>

              <div
                className="flex flex-wrap gap-2"
                data-testid="td-dryrun-summary"
              >
                {Object.entries(dryRunResult.summary || {}).map(([k, v]) => (
                  <Badge
                    key={k}
                    variant="outline"
                    className="border-slate-500/40 text-slate-200 bg-slate-500/10"
                    data-testid={`td-dryrun-summary-${k}`}
                  >
                    {k}: <span className="ml-1 font-semibold">{v}</span>
                  </Badge>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 items-center text-xs">
                <span className="text-muted-foreground">Filter:</span>
                {[
                  "all",
                  "changes",
                  "unchanged",
                  "proposed_update",
                  "proposed_deactivate",
                  "proposed_delete",
                  "proposed_create",
                  "blocked",
                  "invalid",
                ].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    data-testid={`td-dryrun-filter-${s}`}
                    className={`px-2 py-0.5 rounded-md border text-xs ${
                      statusFilter === s
                        ? "border-indigo-400 bg-indigo-500/20 text-indigo-100"
                        : "border-slate-700 hover:border-slate-500"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3 border-t pt-3 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectAll}
                  disabled={selectableRowNumbers.length === 0}
                  data-testid="td-apply-select-all-btn"
                >
                  {selectedRows.size === selectableRowNumbers.length &&
                  selectableRowNumbers.length > 0
                    ? "Clear selection"
                    : `Select all safe (${selectableRowNumbers.length})`}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {selectedRows.size} of {selectableRowNumbers.length}{" "}
                  selectable row(s) chosen.
                </span>
                <Button
                  onClick={openConfirm}
                  disabled={selectedRows.size === 0 || applyBusy}
                  data-testid="td-apply-open-confirm-btn"
                  className="ml-auto"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Apply selected safe changes
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  data-testid="td-dryrun-row-table"
                >
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2 pr-3 w-8">Apply</th>
                      <th className="py-2 pr-3">Row</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Diffs</th>
                      <th className="py-2 pr-3">Warnings / errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDryRunRows.slice(0, 200).map((r, idx) => {
                      const isSelectable = SELECTABLE_STATUSES.has(r.status);
                      return (
                      <tr
                        key={`${r.row_number}-${idx}`}
                        className="border-b last:border-b-0 align-top"
                        data-testid={`td-dryrun-row-${r.row_number}`}
                      >
                        <td className="py-2 pr-3">
                          {isSelectable ? (
                            <Checkbox
                              checked={selectedRows.has(r.row_number)}
                              onCheckedChange={() => toggleRow(r.row_number)}
                              data-testid={`td-apply-row-checkbox-${r.row_number}`}
                            />
                          ) : (
                            <span
                              className="inline-block w-4 h-4 rounded border border-slate-700 opacity-30"
                              title="Not selectable in this version"
                              data-testid={`td-apply-row-disabled-${r.row_number}`}
                            />
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {r.row_number}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`px-2 py-0.5 rounded-md text-xs border ${
                              r.status === "unchanged"
                                ? "border-slate-500/40 text-slate-200 bg-slate-500/10"
                                : r.status === "proposed_update"
                                ? "border-indigo-500/40 text-indigo-200 bg-indigo-500/10"
                                : r.status === "proposed_deactivate"
                                ? "border-orange-500/40 text-orange-200 bg-orange-500/10"
                                : r.status === "proposed_delete"
                                ? "border-rose-500/40 text-rose-200 bg-rose-500/10"
                                : r.status === "proposed_create"
                                ? "border-emerald-500/40 text-emerald-200 bg-emerald-500/10"
                                : r.status === "blocked"
                                ? "border-rose-600/60 text-rose-100 bg-rose-600/20"
                                : "border-amber-500/40 text-amber-200 bg-amber-500/10"
                            }`}
                            data-testid={`td-dryrun-row-${r.row_number}-status`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="py-2 pr-3 font-medium">
                          {r.current_name || r.uploaded_name || "—"}
                          {r.uploaded_name &&
                            r.current_name &&
                            r.uploaded_name !== r.current_name && (
                              <div className="text-xs text-muted-foreground">
                                uploaded as: {r.uploaded_name}
                              </div>
                            )}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          {(r.diffs || []).length === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <ul className="space-y-0.5">
                              {(r.diffs || []).map((d, i) => (
                                <li key={i}>
                                  <span className="text-slate-300">
                                    {d.field}:
                                  </span>{" "}
                                  <span className="text-rose-300">
                                    {String(d.from)}
                                  </span>{" "}
                                  →{" "}
                                  <span className="text-emerald-300">
                                    {String(d.to)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {(r.errors || []).map((e, i) => (
                            <div key={`e${i}`} className="text-rose-300">
                              {e}
                            </div>
                          ))}
                          {(r.warnings || []).map((w, i) => (
                            <div key={`w${i}`}>{w}</div>
                          ))}
                          {(r.errors || []).length === 0 &&
                            (r.warnings || []).length === 0 &&
                            "—"}
                        </td>
                      </tr>
                      );
                    })}
                    {filteredDryRunRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-4 text-center text-muted-foreground"
                        >
                          No rows match this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {filteredDryRunRows.length > 200 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Showing first 200 rows. Download JSON for the full set.
                  </p>
                )}
              </div>
            </div>
          )}

          {applyResult && (
            <div
              className="space-y-3 border-t pt-4 mt-4"
              data-testid="td-apply-result"
            >
              <div className="flex flex-wrap gap-2 items-center">
                <Badge
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-200 bg-emerald-500/10"
                  data-testid="td-apply-result-badge"
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Apply complete · applied {applyResult.summary.applied} ·
                  skipped {applyResult.summary.skipped} · failed{" "}
                  {applyResult.summary.failed}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {applyResult.generated_at}
                </span>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-300">
                  Ledger ({applyResult.summary.applied +
                    applyResult.summary.skipped +
                    applyResult.summary.failed}{" "}
                  rows)
                </summary>
                <pre
                  className="bg-slate-900/40 p-3 rounded mt-2 overflow-auto max-h-96"
                  data-testid="td-apply-ledger-pre"
                >
                  {JSON.stringify(
                    {
                      applied: applyResult.applied,
                      skipped: applyResult.skipped,
                      failed: applyResult.failed,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent
          className="max-w-2xl"
          data-testid="td-import-help-dialog"
        >
          <DialogHeader>
            <DialogTitle>How Task Definitions Export / Import works</DialogTitle>
            <DialogDescription>
              Safe export → edit → dry-run → apply workflow. No changes
              happen until you confirm Apply.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <ol className="list-decimal pl-5 space-y-1">
              <li><b>Download XLSX</b> — exports the catalogue with dependency counts.</li>
              <li><b>Edit</b> the <code>Task Definitions</code> sheet in Excel.</li>
              <li><b>Preserve IDs</b> for existing rows. Never change the <code>ID</code> column.</li>
              <li>Use <code>Action=create</code> for new rows (blank ID).</li>
              <li>Use <code>Action=deactivate</code> for unused rows — never delete.</li>
              <li><b>Upload for dry-run preview</b>.</li>
              <li>Review warnings, errors, and dependency counts.</li>
              <li>Select only the <b>safe rows</b> you want applied.</li>
              <li>Click <b>Apply selected safe changes</b> and confirm.</li>
              <li>Check the <b>Audit log</b> for an append-only ledger.</li>
            </ol>
            <div className="border border-amber-500/30 bg-amber-500/10 rounded p-3 text-amber-100 text-xs space-y-1">
              <p className="font-semibold flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Safety guarantees
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>IDs must not be changed.</li>
                <li><code>company_id</code> is always forced from your tenant — never read from the workbook.</li>
                <li>Hard <b>delete is blocked</b> in this version.</li>
                <li>Referenced rows cannot be deactivated.</li>
                <li>Protected/system seeds cannot be edited.</li>
                <li>Rate changes affect future / new line items only — historical snapshots are not rewritten.</li>
                <li>Cross-tenant IDs cannot be applied.</li>
                <li>The dry-run is always re-run server-side at apply time — stale dry-runs are rejected.</li>
                <li>No emails are sent by this workflow.</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setHelpOpen(false)} data-testid="td-import-help-close-btn">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="max-w-2xl"
          data-testid="td-apply-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle>Apply selected safe changes?</DialogTitle>
            <DialogDescription>
              The server will re-run the dry-run before writing. Only rows
              still classified as safe in that re-run will be applied.
              Delete and merge are not supported in this version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              You have selected{" "}
              <b data-testid="td-apply-confirm-selected-count">
                {selectedRows.size}
              </b>{" "}
              row(s).
            </p>
            <div className="max-h-64 overflow-auto rounded border border-slate-800 p-2 text-xs">
              {Array.from(selectedRows)
                .sort((a, b) => a - b)
                .map((rn) => {
                  const r = (dryRunResult?.rows || []).find(
                    (x) => x.row_number === rn
                  );
                  if (!r) return null;
                  return (
                    <div
                      key={rn}
                      className="py-1 border-b last:border-b-0"
                      data-testid={`td-apply-confirm-row-${rn}`}
                    >
                      <span className="text-muted-foreground">#{rn}</span>{" "}
                      <span className="font-semibold">{r.status}</span> ·{" "}
                      {r.current_name || r.uploaded_name}
                    </div>
                  );
                })}
            </div>
            <label className="flex items-start gap-2 text-xs">
              <Checkbox
                checked={confirmAck}
                onCheckedChange={(v) => setConfirmAck(Boolean(v))}
                data-testid="td-apply-confirm-ack-checkbox"
              />
              <span>
                I understand that IDs are preserved, deletes/merges are
                NOT supported, and historical line-item snapshots are
                NOT rewritten by this apply.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={applyBusy}
              data-testid="td-apply-confirm-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={runApply}
              disabled={!confirmAck || applyBusy}
              data-testid="td-apply-confirm-apply-btn"
            >
              {applyBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Apply {selectedRows.size} row(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent
          className="max-w-4xl"
          data-testid="td-import-audit-dialog"
        >
          <DialogHeader>
            <DialogTitle>Task Definitions import audit log</DialogTitle>
            <DialogDescription>
              Append-only ledger of every apply attempt — success, skip,
              or failure. Tenant-scoped.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto max-h-[60vh]">
            <table
              className="w-full text-xs"
              data-testid="td-import-audit-table"
            >
              <thead>
                <tr className="border-b text-left uppercase text-muted-foreground">
                  <th className="py-1 pr-3">When</th>
                  <th className="py-1 pr-3">Actor</th>
                  <th className="py-1 pr-3">Op</th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">TD ID</th>
                  <th className="py-1 pr-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-3 text-center text-muted-foreground">
                      No apply attempts yet.
                    </td>
                  </tr>
                )}
                {auditRows.map((r) => (
                  <tr key={r.id} className="border-b last:border-b-0 align-top">
                    <td className="py-1 pr-3 whitespace-nowrap text-muted-foreground">
                      {r.timestamp}
                    </td>
                    <td className="py-1 pr-3">{r.actor_email || "—"}</td>
                    <td className="py-1 pr-3">{r.operation}</td>
                    <td className="py-1 pr-3">
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs ${
                          r.apply_status === "applied"
                            ? "bg-emerald-500/10 text-emerald-200"
                            : r.apply_status === "failed"
                            ? "bg-rose-500/10 text-rose-200"
                            : "bg-amber-500/10 text-amber-200"
                        }`}
                      >
                        {r.apply_status}
                      </span>
                    </td>
                    <td className="py-1 pr-3 font-mono text-xs">
                      {r.task_definition_id || "—"}
                    </td>
                    <td className="py-1 pr-3 text-muted-foreground">
                      {r.reason || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button onClick={() => setAuditOpen(false)} data-testid="td-import-audit-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
